/**
 * Panel de **historial guardado** (réplica de `renderHistoryPanel` + `loadHistory` del
 * SSR).
 *
 * Se carga a demanda (botón "Cargar historial"): la query (`useCutlistJobs`) solo se
 * habilita al desplegar, igual que el SSR (que no lo pedía al entrar). Cada job lista su
 * nombre, fecha, tableros y % de desperdicio, con un botón "Cargar" que restaura su
 * entrada y resultado en el editor. Presentacional + orquestación de hooks (no hace
 * `fetch`). Estados de carga/error contemplados.
 */
import { useState } from "react";

import { Button } from "@/components/ui";

import { cutlistErrorMessage, formatJobDate } from "../format";
import { useCutlistJobs, useReloadCutlistJob } from "../hooks/useCutlist";
import type { CutlistJob } from "../types";

interface HistoryPanelProps {
  /** Restaura un job recargado en el editor (entrada + resultado). */
  onReload: (job: CutlistJob) => void;
}

export function HistoryPanel({ onReload }: HistoryPanelProps) {
  const [open, setOpen] = useState(false);
  const jobsQuery = useCutlistJobs(open);
  const reload = useReloadCutlistJob();

  function handleReload(jobId: number) {
    reload.mutate(jobId, {
      onSuccess: (job) => {
        onReload(job);
      },
    });
  }

  return (
    <div className="flex flex-col gap-2">
      <Button
        type="button"
        variant="secondary"
        size="sm"
        className="w-full"
        onClick={() => setOpen(true)}
      >
        Cargar historial
      </Button>

      {!open ? (
        <p className="text-sm text-slate-500">
          Pulsa &quot;Cargar historial&quot; para ver tus optimizaciones guardadas.
        </p>
      ) : jobsQuery.isPending ? (
        <p className="text-sm text-slate-500" role="status">
          Cargando…
        </p>
      ) : jobsQuery.isError ? (
        <p className="text-sm text-red-600" role="alert">
          {cutlistErrorMessage(jobsQuery.error)}
        </p>
      ) : jobsQuery.data.items.length === 0 ? (
        <p className="text-sm text-slate-500">
          No hay optimizaciones guardadas todavía.
        </p>
      ) : (
        <ul className="flex flex-col gap-2">
          {jobsQuery.data.items.map((job) => {
            const fecha = formatJobDate(job.created_at);
            const sheets =
              job.sheets_used != null
                ? `${job.sheets_used} tablero${job.sheets_used !== 1 ? "s" : ""}`
                : "";
            const waste = job.waste_pct != null ? `${job.waste_pct}% desp.` : "";
            const meta = [fecha, sheets, waste].filter(Boolean).join(" · ");
            return (
              <li
                key={job.id}
                className="flex items-center justify-between gap-2 rounded-md border border-slate-200 px-3 py-2"
              >
                <div className="min-w-0">
                  <div className="truncate text-sm font-medium text-slate-700">
                    {job.nombre || `Optimización #${job.id}`}
                  </div>
                  <div className="truncate text-xs text-slate-500">{meta}</div>
                </div>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  className="flex-shrink-0"
                  disabled={reload.isPending}
                  onClick={() => handleReload(job.id)}
                >
                  Cargar
                </Button>
              </li>
            );
          })}
        </ul>
      )}

      {reload.isError ? (
        <p className="text-sm text-red-600" role="alert">
          {cutlistErrorMessage(reload.error)}
        </p>
      ) : null}
    </div>
  );
}
